import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAppointment } from '../../contexts/AppointmentContext';
import { Calendar, Clock, User, FileText, Baby, Heart, ArrowLeft, CreditCard, CheckCircle, Video, Phone, MessageSquare, Lock, Loader } from 'lucide-react';

const BookAppointment = () => {
  const { user } = useAuth();
  const { bookAppointment } = useAppointment();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1: Form, 2: Payment, 3: Success
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    patientType: 'mother',
    date: '',
    time: '',
    reason: '',
    notes: '',
    mode: 'video',
  });

  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });

  const motherReasons = [
    'First Antenatal',
    'Follow Up Antenatal',
    'Post Natal',
    'Others'
  ];

  const childReasons = [
    'Fever',
    'Cough',
    'Diarrhoea',
    'Skin Rashes',
    'Ear Infection',
    'Growth',
    'Nutrition',
    'Cord Dressing',
    'Breast Milk Advice'
  ];

  const availableTimes = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00'
  ];

  const consultationFees = {
    video: 1200,
    audio: 800,
    chat: 500
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setStep(2); // Move to payment step
  };

  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    setIsProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      // Create appointment after successful payment
      const appointment = {
        id: Math.random().toString(36).substr(2, 9),
        userId: user.id,
        patientType: formData.patientType,
        type: formData.reason,
        date: formData.date,
        time: formData.time,
        mode: formData.mode,
        notes: formData.notes,
        status: 'pending',
        doctor: 'Dr. Sarah Johnson',
        specialty: formData.patientType === 'mother' ? 'Maternal Care' : 'Pediatrics',
        amount: consultationFees[formData.mode],
        createdAt: new Date().toISOString(),
        paymentStatus: 'paid'
      };

      bookAppointment(appointment);
      setIsProcessing(false);
      setStep(3); // Move to success step
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/patient/appointments');
      }, 3000);
    }, 2000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset reason when patient type changes
      ...(name === 'patientType' ? { reason: '' } : {})
    }));
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({ ...prev, [name]: value }));
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'video': return <Video className="w-5 h-5" />;
      case 'audio': return <Phone className="w-5 h-5" />;
      case 'chat': return <MessageSquare className="w-5 h-5" />;
      default: return <Video className="w-5 h-5" />;
    }
  };

  const reasons = formData.patientType === 'mother' ? motherReasons : childReasons;

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <button
            onClick={() => step === 1 ? navigate('/patient/appointments') : setStep(step - 1)}
            className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-4 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            {step === 1 ? 'Back to Appointments' : 'Back'}
          </button>
          <div className="flex items-center gap-3 md:gap-4 mb-2">
            <div className="p-2 md:p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl md:rounded-2xl shadow-lg shadow-emerald-500/30">
              {step === 1 ? <Calendar className="w-6 h-6 md:w-8 md:h-8 text-white" /> : 
               step === 2 ? <CreditCard className="w-6 h-6 md:w-8 md:h-8 text-white" /> :
               <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-white" />}
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                {step === 1 ? 'Book Telemedicine Appointment' : 
                 step === 2 ? 'Payment' : 'Booking Confirmed!'}
              </h1>
              <p className="text-sm md:text-base text-gray-600 mt-1">
                {step === 1 ? 'Schedule your online healthcare consultation' :
                 step === 2 ? 'Secure payment for your consultation' :
                 'Your appointment is confirmed'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        {step === 1 && (
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
          <form onSubmit={handleFormSubmit} className="space-y-6">
            {/* Patient Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                <User className="w-4 h-4 inline mr-2" />
                Appointment For
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, patientType: 'mother', reason: '' }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.patientType === 'mother'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Heart className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">Mother</div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, patientType: 'child', reason: '' }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.patientType === 'child'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Baby className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">Child</div>
                </button>
              </div>
            </div>

            {/* Reason */}
            <div>
              <label htmlFor="reason" className="block text-sm font-semibold text-gray-700 mb-2">
                <FileText className="w-4 h-4 inline mr-2" />
                Reason for Appointment *
              </label>
              <select
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
              >
                <option value="">Select a reason</option>
                {reasons.map(reason => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div>
              <label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Preferred Date *
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
              />
            </div>

            {/* Time */}
            <div>
              <label htmlFor="time" className="block text-sm font-semibold text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-2" />
                Preferred Time *
              </label>
              <select
                id="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
              >
                <option value="">Select a time</option>
                {availableTimes.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>

            {/* Consultation Mode */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Consultation Mode *
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, mode: 'video' }))}
                  className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    formData.mode === 'video'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  Video Call
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, mode: 'audio' }))}
                  className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    formData.mode === 'audio'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  Audio Call
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, mode: 'chat' }))}
                  className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                    formData.mode === 'chat'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  Chat
                </button>
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-2">
                Additional Notes (Optional)
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                placeholder="Any additional information you'd like to share with the doctor..."
              />
            </div>

            {/* Summary */}
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
              <h3 className="font-semibold text-emerald-900 mb-2">Consultation Fee</h3>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">
                  {formData.mode === 'video' ? 'Video Consultation' :
                   formData.mode === 'audio' ? 'Audio Consultation' : 'Chat Consultation'}
                </span>
                <span className="text-2xl font-bold text-emerald-600">
                  ₦{consultationFees[formData.mode].toLocaleString()}
                </span>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all shadow-md shadow-emerald-500/30"
            >
              Proceed to Payment
            </button>
          </form>
        </div>
        )}

        {/* Step 2: Payment */}
        {step === 2 && (
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
          {/* Order Summary */}
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-3">Appointment Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Date:</span>
                <span className="font-medium">{new Date(formData.date).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Time:</span>
                <span className="font-medium">{formData.time}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Type:</span>
                <span className="font-medium">{formData.reason}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Consultation Mode:</span>
                <span className="font-medium capitalize flex items-center gap-1">
                  {getModeIcon(formData.mode)}
                  {formData.mode}
                </span>
              </div>
              <div className="border-t-2 border-emerald-200 pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-900">Total Amount:</span>
                  <span className="text-2xl font-bold text-emerald-600">
                    ₦{consultationFees[formData.mode].toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <form onSubmit={handlePaymentSubmit} className="space-y-5">
            <div className="flex items-center gap-2 text-gray-600 mb-4">
              <Lock className="w-4 h-4" />
              <span className="text-sm">Secure payment powered by Paystack</span>
            </div>

            <div>
              <label htmlFor="cardName" className="block text-sm font-medium text-gray-700 mb-2">
                Cardholder Name
              </label>
              <input
                type="text"
                id="cardName"
                name="cardName"
                value={paymentData.cardName}
                onChange={handlePaymentChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-2">
                Card Number
              </label>
              <input
                type="text"
                id="cardNumber"
                name="cardNumber"
                value={paymentData.cardNumber}
                onChange={handlePaymentChange}
                required
                maxLength="16"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder="1234 5678 9012 3456"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Expiry Date
                </label>
                <input
                  type="text"
                  id="expiryDate"
                  name="expiryDate"
                  value={paymentData.expiryDate}
                  onChange={handlePaymentChange}
                  required
                  placeholder="MM/YY"
                  maxLength="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                />
              </div>
              <div>
                <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-2">
                  CVV
                </label>
                <input
                  type="text"
                  id="cvv"
                  name="cvv"
                  value={paymentData.cvv}
                  onChange={handlePaymentChange}
                  required
                  maxLength="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  placeholder="123"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isProcessing}
              className="w-full px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all shadow-md shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Processing Payment...
                </>
              ) : (
                <>
                  Pay ₦{consultationFees[formData.mode].toLocaleString()}
                </>
              )}
            </button>
          </form>
        </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/30">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Payment Successful!</h2>
          <p className="text-lg text-gray-600 mb-6">
            Your telemedicine appointment has been confirmed
          </p>

          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6 mb-6 text-left max-w-md mx-auto">
            <h3 className="font-semibold text-emerald-900 mb-4">Appointment Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Date & Time:</span>
                <span className="font-medium">{new Date(formData.date).toLocaleDateString()} at {formData.time}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Doctor:</span>
                <span className="font-medium">Dr. Sarah Johnson</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Consultation Mode:</span>
                <span className="font-medium capitalize flex items-center gap-1">
                  {getModeIcon(formData.mode)}
                  {formData.mode}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount Paid:</span>
                <span className="font-bold text-emerald-600">₦{consultationFees[formData.mode].toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-600 mb-6">
            <p>You will receive a confirmation email shortly.</p>
            <p className="mt-2">The doctor will join the call at the scheduled time.</p>
          </div>

          <button
            onClick={() => navigate('/patient/appointments')}
            className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all shadow-md shadow-emerald-500/30"
          >
            View My Appointments
          </button>

          <p className="text-xs text-gray-500 mt-4">Redirecting automatically in 3 seconds...</p>
        </div>
        )}
      </div>
    </div>
  );
};

export default BookAppointment;
